import orgApi from '@api/orgApi';
import roleApi from '@api/roleApi';
import ClaimSelect from '@components/claimSelect';
import ItemEditor from '@components/itemEditor';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { InputWrapper, TextInput } from '@mantine/core';
import type { Role } from '@models/role';
import { createFileRoute } from '@tanstack/react-router';
import { isGlobalOrg, isGlobalRole, roleName } from '@util/globalEntities';
import { createForm } from 'cross-state/react';

export const Route = createFileRoute('/profile/orgs/$orgId/roles/$id')({
  component: RoleEditor,
});

const defaultValue: Role = {
  id: '',
  orgId: '',
  name: '',
  claims: [],
};

const roleForm = createForm<Role>({
  defaultValue,
  validations: {
    orgId: { required: (x) => !!x },
    name: { required: (x) => !!x },
    // locations/read comes with every role, so it does not count. Older roles without any keep working unless
    // their claims change.
    claims: {
      atLeastOneClaim: (x, { original }) =>
        !!x?.some((claim) => claim !== 'locations/read') ||
        (!!original && x?.length === original.claims.length && original.claims.every((claim) => x.includes(claim))),
    },
  },
});

function RoleEditor() {
  const t = useTranslator();
  const { orgId, id: roleId } = Route.useParams();
  const [org] = orgApi.get(orgId).useCache();
  const { hasClaim } = useAuth();

  return (
    <ItemEditor
      domain="roles"
      api={roleApi}
      // Global roles are defined by the service and reconciled on every start, so they are read only
      readonly={(role) =>
        role ? isGlobalRole(role) || !hasClaim('roles/write', role.orgId) : !hasClaim('roles/create', orgId)
      }
      deletable={!isGlobalOrg(orgId) && hasClaim('roles/delete', orgId)}
      back={{ to: '/profile/orgs/$id', params: { id: orgId } }}
      form={roleForm}
      name={(role) => roleName(t, role)}
      formProps={{
        defaultValue: {
          ...defaultValue,
          orgId: orgId ?? '',
        },
      }}
    >
      {({ item }) => (
        <>
          <InputWrapper label={t('roles.properties.org.label')}>
            <div>{org?.name ?? orgId}</div>
          </InputWrapper>

          <roleForm.Field
            name="name"
            render={(props) => (
              <TextInput
                {...props}
                value={isGlobalRole(item) ? roleName(t, item) : props.value}
                label={t('roles.properties.name.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <roleForm.Field
            name="claims"
            render={(props) => (
              <ClaimSelect
                {...props}
                label={t('roles.properties.claims.label')}
                orgId={orgId}
                roleId={roleId === 'create' ? undefined : roleId}
              />
            )}
          />
        </>
      )}
    </ItemEditor>
  );
}
